import { makeStyles } from '@material-ui/core'
import { useCountryListsTranslated } from 'hooks/useCountryListsTranslated'
import { CountryFlagIcon, FieldTitle, styled } from 'opensolar-ui'
import { CountryMapType } from 'pages/mfa/types'
import { Validator } from 'ra-core'
import React from 'react'
import { SelectInput, TextInput, useTranslate } from 'react-admin'
import { Theme } from 'types/themes'

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
    gap: 8,
    alignItems: 'end',
  },
  countrySelect: {
    minWidth: 150,
    [theme.breakpoints.down('sm')]: {
      minWidth: 110,
      flexShrink: 0,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 125,
      flexShrink: 0,
    },
  },
  flagRow: {
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginRight: 5,
    },
    paddingRight: 20,
  },
  flag: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 5,
  },
}))

const FullWidth = styled('div')({
  width: '100%',
  marginTop: 8,
})

const StyledTextInput = styled(TextInput)(({ theme }) => ({
  '& .OSUI-InputBase-root': {
    backgroundColor: theme.palette.neutralGrey[100],
  },
}))

const StyledSelectInput = styled(SelectInput)(({ theme }) => ({
  '& .OSUI-InputBase-root': {
    backgroundColor: theme.palette.neutralGrey[100],
  },
}))

export const PHONE_MAX_LENGTH = 20

export const validatePhoneMaxLength = (value: string) => {
  if (value && value.length > PHONE_MAX_LENGTH) {
    return {
      message: 'Ensure this field has no more than %{max} characters.',
      args: { max: PHONE_MAX_LENGTH },
    }
  }
}

interface PropTypes {
  source: string
  initialCountry?: string
  label?: string
  disabled?: boolean
  isFullWidth?: boolean
  validate?: Validator[]
  labelAdornment?: string
  renderPhoneCodeValue?: boolean
  onFocus?: (event: any) => void
  hideCountrySelect?: boolean
}

const PhoneField: React.FC<PropTypes> = (props) => {
  const classes = useStyles()
  const translate = useTranslate()
  const { countriesAll } = useCountryListsTranslated()
  const initialCountryObject = Object.values(countriesAll).find((x) => x.name === props.initialCountry)
  return (
    <FullWidth>
      {props.label && <FieldTitle labelAdornment={props?.labelAdornment} label={translate(props.label)} />}
      <div className={classes.wrapper}>
        {!props?.hideCountrySelect && (
          <StyledSelectInput
            label={false}
            margin="none"
            source={`${props.source}.custom_data.os_meta.phone_country_iso2`}
            variant="outlined"
            optionValue="iso2"
            id="phone-country-selector"
            defaultValue={initialCountryObject?.iso2 ?? ''}
            className={classes.countrySelect}
            SelectProps={{
              style: {
                minWidth: 90,
              },
              MenuProps: {
                PaperProps: {
                  style: {
                    maxWidth: 'none',
                    width: 'auto',
                    minWidth: 250,
                  },
                },
              },
              renderValue: (value) => {
                const country = Object.values(countriesAll).find((x) => x.iso2 === value)
                if (country) {
                  return (
                    <div className={classes.flagRow}>
                      <>
                        {country.iso2 && (
                          <div className={classes.flag}>
                            <CountryFlagIcon code={country.iso2} />
                          </div>
                        )}
                        {props?.renderPhoneCodeValue ? `+(${country.prefix})` : value}
                      </>
                    </div>
                  )
                }
              },
            }}
            optionText={(country: unknown) => {
              const countryType = country as CountryMapType & { translatedName: string }
              if (countryType == null) return ''
              return `${countryType.translatedName || countryType.name} (${countryType.prefix})`
            }}
            choices={Object.values(countriesAll).filter((x) => x.prefix)}
            disabled={props.disabled}
          />
        )}
        <StyledTextInput
          margin="none"
          source={props.source + '.phone'}
          variant="outlined"
          label={false}
          disabled={props.disabled}
          // fullWidth={props.isFullWidth}
          id="phone-input"
          validate={[validatePhoneMaxLength, ...(props?.validate || [])]}
          onFocus={props.onFocus}
          style={{ flexGrow: 1 }}
        />
      </div>
    </FullWidth>
  )
}

export default PhoneField
